/**
 * 추가폰 등록 관련 서비스
 */

import { ref, get, set, update, remove } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import {
  AccountGroup,
  PhoneToGroup,
  AccountSyncHistory,
  PhoneSearchResult,
  GroupMemberInfo,
  AccountSyncErrorCode,
  AccountSyncAction,
} from '../types/accountSync';
import {
  ACCOUNT_SYNC_PATHS,
  DEFAULT_GROUP_SETTINGS,
  ERROR_MESSAGES,
} from '../config/accountSyncConfig';
import { AccountSyncTransaction, batchUpdate } from '../utils/accountSyncTransaction';
import AccountSyncValidator from '../utils/accountSyncValidation';

class AccountSyncService {
  /**
   * 전화번호로 기사 검색
   */
  async searchDriversByPhone(query: string): Promise<PhoneSearchResult[]> {
    if (query.length < DEFAULT_GROUP_SETTINGS.MIN_SEARCH_LENGTH) {
      throw new Error(`최소 ${DEFAULT_GROUP_SETTINGS.MIN_SEARCH_LENGTH}자리 이상 입력해주세요.`);
    }

    const driversRef = ref(database, DB_PATHS.DRIVERS);
    const snapshot = await get(driversRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    const drivers = snapshot.val();
    const results: PhoneSearchResult[] = [];

    // 전화번호에 query가 포함된 기사 찾기
    for (const [phoneNumber, driver] of Object.entries(drivers) as [string, any][]) {
      if (phoneNumber.includes(query)) {
        // 해당 기사가 이미 그룹에 속해있는지 확인
        const isInGroup = await this.checkIfInGroup(phoneNumber);
        
        results.push({
          username: driver.username || 'Unknown',
          phonenumber: phoneNumber,
          expiredate: driver.expiredate || '',
          isInGroup,
          groupId: isInGroup ? await this.getGroupId(phoneNumber) : undefined,
        });
      }
    }

    return results;
  }

  /**
   * 그룹에 속해있는지 확인
   */
  async checkIfInGroup(phoneNumber: string): Promise<boolean> {
    const phoneToGroupRef = ref(database, `${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${phoneNumber}`);
    const snapshot = await get(phoneToGroupRef);
    return snapshot.exists();
  }

  /**
   * 그룹 ID 가져오기
   */
  async getGroupId(phoneNumber: string): Promise<string | undefined> {
    const phoneToGroupRef = ref(database, `${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${phoneNumber}`);
    const snapshot = await get(phoneToGroupRef);
    
    if (snapshot.exists()) {
      return snapshot.val().groupId;
    }
    
    return undefined;
  }

  /**
   * 그룹 멤버 조회
   */
  async getGroupMembers(phoneNumber: string): Promise<GroupMemberInfo[]> {
    const groupId = await this.getGroupId(phoneNumber);
    
    if (!groupId) {
      return [];
    }

    const groupRef = ref(database, `${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`);
    const groupSnapshot = await get(groupRef);
    
    if (!groupSnapshot.exists()) {
      return [];
    }

    const group = groupSnapshot.val() as AccountGroup;
    const members: GroupMemberInfo[] = [];

    // 각 멤버의 정보 가져오기
    for (const memberPhone of group.members) {
      const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${memberPhone}`);
      const driverSnapshot = await get(driverRef);
      
      if (driverSnapshot.exists()) {
        const driver = driverSnapshot.val();
        
        // phoneToGroup에서 가입일 가져오기
        const phoneToGroupRef = ref(database, `${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${memberPhone}`);
        const phoneToGroupSnapshot = await get(phoneToGroupRef);
        const joinedAt = phoneToGroupSnapshot.exists() 
          ? phoneToGroupSnapshot.val().joinedAt 
          : group.createdAt;

        members.push({
          phoneNumber: memberPhone,
          username: driver.username || 'Unknown',
          expiredate: driver.expiredate || '',
          joinedAt,
          isPrimary: memberPhone === group.primaryPhone,
        });
      }
    }

    return members;
  }

  /**
   * 그룹에 추가
   */
  async addToGroup(primaryPhone: string, phonesToAdd: string[], executedBy: string): Promise<void> {
    // 권한 검증은 UI 레벨에서 처리됨
    
    // 그룹 ID 확인
    let groupId = await this.getGroupId(primaryPhone);
    let group: AccountGroup;

    if (!groupId) {
      // 새 그룹 생성
      groupId = this.generateGroupId();
      group = {
        groupId,
        members: [primaryPhone],
        primaryPhone,
        createdAt: new Date().toISOString(),
        createdBy: executedBy,
        lastUpdated: new Date().toISOString(),
        updatedBy: executedBy,
      };
    } else {
      // 기존 그룹 가져오기
      const groupRef = ref(database, `${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`);
      const groupSnapshot = await get(groupRef);
      
      if (!groupSnapshot.exists()) {
        throw new Error(ERROR_MESSAGES.E007);
      }
      
      group = groupSnapshot.val() as AccountGroup;
    }

    // 그룹 크기 확인
    if (group.members.length + phonesToAdd.length > DEFAULT_GROUP_SETTINGS.MAX_GROUP_SIZE) {
      throw new Error(ERROR_MESSAGES.E002);
    }

    // 중복 확인
    for (const phone of phonesToAdd) {
      if (await this.checkIfInGroup(phone)) {
        throw new Error(`${phone}는 ${ERROR_MESSAGES.E004}`);
      }
    }

    // 주계정의 expiredate 가져오기
    let primaryExpireDate: string | null = null;
    const primaryDriverRef = ref(database, `${DB_PATHS.DRIVERS}/${primaryPhone}`);
    const primaryDriverSnapshot = await get(primaryDriverRef);
    
    if (primaryDriverSnapshot.exists()) {
      const primaryDriverData = primaryDriverSnapshot.val();
      primaryExpireDate = primaryDriverData.expiredate || null;
      console.log(`주계정 ${primaryPhone}의 만료일: ${primaryExpireDate}`);
    }

    // 트랜잭션으로 업데이트
    const updates: { [key: string]: any } = {};

    // 그룹에 멤버 추가
    const updatedMembers = [...new Set([...group.members, ...phonesToAdd])];
    group.members = updatedMembers;
    group.lastUpdated = new Date().toISOString();
    group.updatedBy = executedBy;

    // 그룹 업데이트
    updates[`${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`] = group;

    // PhoneToGroup 매핑 추가 - 새 그룹인 경우 primaryPhone도 추가
    if (!await this.checkIfInGroup(primaryPhone)) {
      updates[`${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${primaryPhone}`] = {
        groupId,
        joinedAt: group.createdAt || new Date().toISOString(),
      };
    }

    // 추가되는 폰들의 PhoneToGroup 매핑
    for (const phone of phonesToAdd) {
      updates[`${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${phone}`] = {
        groupId,
        joinedAt: new Date().toISOString(),
      };
    }

    // 추가되는 폰들에 주계정의 expiredate 적용
    if (primaryExpireDate) {
      for (const phone of phonesToAdd) {
        // 추가되는 폰의 드라이버 정보가 있으면 expiredate 업데이트
        const targetDriverRef = ref(database, `${DB_PATHS.DRIVERS}/${phone}`);
        const targetDriverSnapshot = await get(targetDriverRef);
        
        if (targetDriverSnapshot.exists()) {
          updates[`${DB_PATHS.DRIVERS}/${phone}/expiredate`] = primaryExpireDate;
          console.log(`${phone}에 주계정의 만료일 ${primaryExpireDate} 적용`);
        }
      }
    }

    // 이력 추가
    for (const phone of phonesToAdd) {
      const historyId = this.generateHistoryId();
      const historyEntry: any = {
        id: historyId,
        groupId,
        action: 'ADD' as AccountSyncAction,
        phoneNumber: phone,
        executedBy,
        executedAt: new Date().toISOString(),
        reason: `그룹에 추가폰 등록`,
      };
      
      // 만료일 동기화가 된 경우 이력에 추가
      if (primaryExpireDate) {
        historyEntry.additionalInfo = `만료일 동기화: ${primaryExpireDate}`;
      }
      
      updates[`${ACCOUNT_SYNC_PATHS.SYNC_HISTORY}/${historyId}`] = historyEntry;
    }

    // 모든 업데이트 적용
    await update(ref(database), updates);
  }

  /**
   * 그룹에서 제거
   */
  async removeFromGroup(primaryPhone: string, phoneToRemove: string, executedBy: string): Promise<void> {
    const groupId = await this.getGroupId(primaryPhone);
    
    if (!groupId) {
      throw new Error(ERROR_MESSAGES.E007);
    }

    const groupRef = ref(database, `${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`);
    const groupSnapshot = await get(groupRef);
    
    if (!groupSnapshot.exists()) {
      throw new Error(ERROR_MESSAGES.E007);
    }

    const group = groupSnapshot.val() as AccountGroup;

    // 멤버에서 제거
    group.members = group.members.filter(m => m !== phoneToRemove);

    const updates: { [key: string]: any } = {};

    if (group.members.length === 0) {
      // 그룹 삭제
      updates[`${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`] = null;
      updates[`${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${primaryPhone}`] = null;
    } else {
      // 그룹 업데이트
      group.lastUpdated = new Date().toISOString();
      group.updatedBy = executedBy;
      updates[`${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`] = group;
    }

    // PhoneToGroup 매핑 제거
    updates[`${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${phoneToRemove}`] = null;

    // 이력 추가
    const historyId = this.generateHistoryId();
    updates[`${ACCOUNT_SYNC_PATHS.SYNC_HISTORY}/${historyId}`] = {
      id: historyId,
      groupId,
      action: 'REMOVE' as AccountSyncAction,
      phoneNumber: phoneToRemove,
      executedBy,
      executedAt: new Date().toISOString(),
      reason: `그룹에서 추가폰 제거`,
    };

    // 모든 업데이트 적용
    await update(ref(database), updates);
  }

  /**
   * 새 그룹 생성
   */
  async createAccountGroup(primaryPhone: string, members: string[], executedBy: string): Promise<string> {
    // 모든 멤버가 그룹에 속하지 않았는지 확인
    for (const member of [...members, primaryPhone]) {
      if (await this.checkIfInGroup(member)) {
        throw new Error(`${member}는 ${ERROR_MESSAGES.E004}`);
      }
    }

    // 그룹 크기 확인
    if (members.length + 1 > DEFAULT_GROUP_SETTINGS.MAX_GROUP_SIZE) {
      throw new Error(ERROR_MESSAGES.E002);
    }

    const groupId = this.generateGroupId();
    const now = new Date().toISOString();
    
    const group: AccountGroup = {
      groupId,
      members: [primaryPhone, ...members],
      primaryPhone,
      createdAt: now,
      createdBy: executedBy,
      lastUpdated: now,
      updatedBy: executedBy,
    };

    const updates: { [key: string]: any } = {};

    // 그룹 생성
    updates[`${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`] = group;

    // PhoneToGroup 매핑 생성
    for (const member of group.members) {
      updates[`${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${member}`] = {
        groupId,
        joinedAt: now,
      };
    }

    // 이력 기록
    const historyId = this.generateHistoryId();
    updates[`${ACCOUNT_SYNC_PATHS.SYNC_HISTORY}/${historyId}`] = {
      id: historyId,
      groupId,
      action: 'CREATE' as AccountSyncAction,
      phoneNumber: primaryPhone,
      executedBy,
      executedAt: now,
      reason: `새 그룹 생성 - 멤버: ${group.members.join(', ')}`,
    };

    await update(ref(database), updates);
    return groupId;
  }

  /**
   * 그룹 삭제
   */
  async deleteAccountGroup(groupId: string, executedBy: string): Promise<void> {
    const groupRef = ref(database, `${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`);
    const groupSnapshot = await get(groupRef);
    
    if (!groupSnapshot.exists()) {
      throw new Error(ERROR_MESSAGES.E007);
    }

    const group = groupSnapshot.val() as AccountGroup;
    const updates: { [key: string]: any } = {};

    // 그룹 삭제
    updates[`${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`] = null;

    // 모든 멤버의 PhoneToGroup 매핑 삭제
    for (const member of group.members) {
      updates[`${ACCOUNT_SYNC_PATHS.PHONE_TO_GROUP}/${member}`] = null;
    }

    // 이력 기록
    const historyId = this.generateHistoryId();
    updates[`${ACCOUNT_SYNC_PATHS.SYNC_HISTORY}/${historyId}`] = {
      id: historyId,
      groupId,
      action: 'DELETE' as AccountSyncAction,
      phoneNumber: group.primaryPhone,
      executedBy,
      executedAt: new Date().toISOString(),
      reason: `그룹 삭제 - 멤버: ${group.members.join(', ')}`,
    };

    await update(ref(database), updates);
  }

  /**
   * 그룹 정보 조회
   */
  async getAccountGroup(phoneNumber: string): Promise<AccountGroup | null> {
    const groupId = await this.getGroupId(phoneNumber);
    
    if (!groupId) {
      return null;
    }

    const groupRef = ref(database, `${ACCOUNT_SYNC_PATHS.ACCOUNT_GROUPS}/${groupId}`);
    const snapshot = await get(groupRef);
    
    if (!snapshot.exists()) {
      return null;
    }

    return snapshot.val() as AccountGroup;
  }

  /**
   * 그룹 ID 생성
   */
  private generateGroupId(): string {
    return `GRP_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 이력 ID 생성
   */
  private generateHistoryId(): string {
    return `HST_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 이력 조회 - 그룹 ID 기준
   */
  async getHistoryByGroup(groupId: string): Promise<AccountSyncHistory[]> {
    const historyRef = ref(database, ACCOUNT_SYNC_PATHS.SYNC_HISTORY);
    const snapshot = await get(historyRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    const allHistory = snapshot.val();
    const histories: AccountSyncHistory[] = [];

    // 그룹 ID로 필터링
    for (const [id, history] of Object.entries(allHistory) as [string, any][]) {
      if (history.groupId === groupId) {
        histories.push(history as AccountSyncHistory);
      }
    }

    return histories;
  }

  /**
   * 이력 조회 - 전화번호 기준
   */
  async getHistoryByPhone(phoneNumber: string): Promise<AccountSyncHistory[]> {
    // 먼저 해당 전화번호가 속한 그룹 찾기
    const groupId = await this.getGroupId(phoneNumber);
    
    if (!groupId) {
      return [];
    }

    return this.getHistoryByGroup(groupId);
  }

  /**
   * 전체 이력 조회 (관리자용)
   */
  async getAllHistory(limit: number = 100): Promise<AccountSyncHistory[]> {
    const historyRef = ref(database, ACCOUNT_SYNC_PATHS.SYNC_HISTORY);
    const snapshot = await get(historyRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    const allHistory = snapshot.val();
    const histories: AccountSyncHistory[] = [];

    for (const [id, history] of Object.entries(allHistory) as [string, any][]) {
      histories.push(history as AccountSyncHistory);
    }

    // 최신 순으로 정렬 후 제한
    return histories
      .sort((a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime())
      .slice(0, limit);
  }

  /**
   * 특정 기간의 이력 조회
   */
  async getHistoryByDateRange(
    startDate: Date,
    endDate: Date,
    groupId?: string
  ): Promise<AccountSyncHistory[]> {
    let histories: AccountSyncHistory[] = [];

    if (groupId) {
      histories = await this.getHistoryByGroup(groupId);
    } else {
      histories = await this.getAllHistory(1000); // 더 많은 이력 로드
    }

    // 날짜 범위로 필터링
    return histories.filter(history => {
      const historyDate = new Date(history.executedAt);
      return historyDate >= startDate && historyDate <= endDate;
    });
  }

  /**
   * 이력 통계 조회
   */
  async getHistoryStatistics(groupId?: string): Promise<{
    totalActions: number;
    actionCounts: { [key in AccountSyncAction]?: number };
    lastModified?: string;
    mostActiveUser?: string;
  }> {
    let histories: AccountSyncHistory[] = [];

    if (groupId) {
      histories = await this.getHistoryByGroup(groupId);
    } else {
      histories = await this.getAllHistory(500);
    }

    const stats = {
      totalActions: histories.length,
      actionCounts: {} as { [key in AccountSyncAction]?: number },
      lastModified: histories[0]?.executedAt,
      mostActiveUser: undefined as string | undefined,
    };

    // 액션별 카운트
    const userCounts: { [key: string]: number } = {};

    for (const history of histories) {
      // 액션 카운트
      stats.actionCounts[history.action] = (stats.actionCounts[history.action] || 0) + 1;
      
      // 사용자별 카운트
      userCounts[history.executedBy] = (userCounts[history.executedBy] || 0) + 1;
    }

    // 가장 활발한 사용자 찾기
    let maxCount = 0;
    for (const [user, count] of Object.entries(userCounts)) {
      if (count > maxCount) {
        maxCount = count;
        stats.mostActiveUser = user;
      }
    }

    return stats;
  }
}

export const accountSyncService = new AccountSyncService();